import { useMemo, useState } from 'react';
import { MetaApp, MetaUser } from '../types/meta.types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
    Plus,
    Pencil,
    Trash2,
    RefreshCw,
    XCircle,
    Power,
    Settings,
    User,
    Bug,
    Loader2,
    Search,
    X,
    ArrowUp,
    ArrowDown,
    ArrowUpDown
} from 'lucide-react';
import { cn } from '@/lib/utils';
import AppUsageModal from './AppUsageModal';
import { debugMetaUserToken } from '@/services/api/connections';
import { useToast } from '@/hooks/use-toast';

type ColunaPerfil = 'perfil' | 'app' | 'status';

interface AccountsListProps {
    apps: MetaApp[];
    /** Apps com is_active=0. "Deletar app" é soft delete, então eles existem. */
    appsInativos?: MetaApp[];
    onReativarApp?: (app: MetaApp) => void;
    reativandoAppId?: number | null;
    authentications: MetaUser[];
    /** Só admin: seletor "de quem são as contas". */
    seletorDono?: React.ReactNode;
    onEditApp: (app: MetaApp) => void;
    onDeleteApp: (app: MetaApp) => void;
    onToggleAppStatus: (app: MetaApp) => void;
    onSync: (auth: MetaUser) => void;
    onRevoke: (auth: MetaUser) => void;
    onCreateApp: () => void;
    onConnectAccount: () => void;
    isAdmin?: boolean;
    currentUserId?: number;
}

export default function AccountsList({
    apps,
    appsInativos = [],
    onReativarApp,
    reativandoAppId = null,
    authentications,
    seletorDono,
    onEditApp,
    onDeleteApp,
    onToggleAppStatus,
    onSync,
    onRevoke,
    onCreateApp,
    onConnectAccount,
    isAdmin = false,
    currentUserId
}: AccountsListProps) {
    const [selectedApp, setSelectedApp] = useState<MetaApp | null>(null);
    const [abaApps, setAbaApps] = useState<'ativos' | 'inativos'>('ativos');
    const [buscaPerfil, setBuscaPerfil] = useState('');
    const [filtroApp, setFiltroApp] = useState<string | null>(null);
    const [filtroStatus, setFiltroStatus] = useState<'ativo' | 'expirado' | null>(null);
    const [ordem, setOrdem] = useState<{ coluna: ColunaPerfil; dir: 'asc' | 'desc' } | null>(null);
    const { toast } = useToast();

    // Clicar no cabeçalho ordena: 1º clique asc (status: ativos primeiro),
    // 2º desc, 3º volta à ordem original.
    function alternarOrdem(coluna: ColunaPerfil) {
        setOrdem(atual => {
            if (!atual || atual.coluna !== coluna) return { coluna, dir: 'asc' };
            if (atual.dir === 'asc') return { coluna, dir: 'desc' };
            return null;
        });
    }

    // Clicar no app ou no status de uma linha filtra a tabela por aquele valor;
    // clicar de novo no mesmo valor solta o filtro.
    const perfisFiltrados = useMemo(() => {
        const termo = buscaPerfil.trim().toLowerCase();
        const filtrados = authentications.filter(auth => {
            if (filtroApp !== null && (auth.app_name ?? '') !== filtroApp) return false;
            if (filtroStatus === 'ativo' && !auth.is_active) return false;
            if (filtroStatus === 'expirado' && auth.is_active) return false;
            if (termo && !(auth.meta_user_name ?? '').toLowerCase().includes(termo)
                && !String(auth.meta_user_id).includes(termo)) return false;
            return true;
        });
        if (!ordem) return filtrados;
        const sinal = ordem.dir === 'asc' ? 1 : -1;
        const porNome = (a: MetaUser, b: MetaUser) =>
            (a.meta_user_name ?? '').localeCompare(b.meta_user_name ?? '', 'pt-BR');
        return [...filtrados].sort((a, b) => {
            let cmp = 0;
            if (ordem.coluna === 'status') cmp = Number(b.is_active) - Number(a.is_active);
            else if (ordem.coluna === 'app') cmp = (a.app_name ?? '').localeCompare(b.app_name ?? '', 'pt-BR');
            else cmp = porNome(a, b);
            return cmp * sinal || porNome(a, b);
        });
    }, [authentications, buscaPerfil, filtroApp, filtroStatus, ordem]);

    function cabecalhoOrdenavel(coluna: ColunaPerfil, rotulo: string) {
        const ativo = ordem?.coluna === coluna;
        const Icone = !ativo ? ArrowUpDown : ordem!.dir === 'asc' ? ArrowUp : ArrowDown;
        return (
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <button
                    type="button"
                    onClick={() => alternarOrdem(coluna)}
                    className={cn('inline-flex items-center gap-1 uppercase tracking-wider hover:text-gray-800', ativo && 'text-gray-900')}
                >
                    {rotulo}
                    <Icone className={cn('h-3 w-3', !ativo && 'opacity-40')} />
                </button>
            </th>
        );
    }

    const temFiltroPerfil = filtroApp !== null || filtroStatus !== null || buscaPerfil.trim() !== '';

    function limparFiltrosPerfil() {
        setBuscaPerfil('');
        setFiltroApp(null);
        setFiltroStatus(null);
    }

    /**
     * Apps visíveis nesta aba.
     *
     * Usuário comum vê os apps que ele criou (user_id == dele) MAIS os de
     * Sistema (user_id null). Os de Sistema entram de propósito: são
     * compartilhados/infra e, se escondidos, some da tela um app que a operação
     * pode estar usando, sem ninguém conseguir mais enxergar.
     *
     * Admin continua vendo tudo — é com essa visão que ele dá suporte a
     * problema de app de outro usuário.
     */
    const podeVer = (app: MetaApp) =>
        isAdmin || currentUserId == null
            || app.user_id === currentUserId || app.user_id === null;

    const ativosVisiveis = useMemo(() => apps.filter(podeVer), [apps, isAdmin, currentUserId]);
    const inativosVisiveis = useMemo(
        () => appsInativos.filter(podeVer), [appsInativos, isAdmin, currentUserId]);

    const appsVisiveis = abaApps === 'ativos' ? ativosVisiveis : inativosVisiveis;
    const totalDaAba = abaApps === 'ativos' ? apps.length : appsInativos.length;
    const appsOcultos = totalDaAba - appsVisiveis.length;

    function getCreatedByLabel(app: MetaApp): string {
        if (app.user_id === null) return 'Sistema';
        return app.owner_name ?? `Usuário #${app.user_id}`;
    }

    function getPurposeLabel(app: MetaApp): string {
        switch (app.purpose) {
            case 'broadcast':
                return 'Broadcast';
            case 'funnel':
                return 'Funil';
            default:
                return 'Ambos';
        }
    }

    async function handleDebugToken(auth: MetaUser) {
        try {
            const result = await debugMetaUserToken(auth.id);
            if (result.valid) {
                toast({ title: 'Token válido ✅' });
            } else {
                toast({ title: `Token inválido: ${result.error ?? 'motivo desconhecido'}`, variant: 'destructive' });
            }
        } catch {
            toast({ title: 'Erro ao testar token do perfil', variant: 'destructive' });
        }
    }

    return (
        <div className="space-y-12">
            {/* Authentications Section */}
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">Perfis Conectados</h1>
                        <p className="mt-2 text-gray-600">Gerencie as contas do Facebook integradas ao sistema.</p>
                    </div>
                    <div className="flex items-center gap-2">
                        {seletorDono}
                        <Button
                            onClick={onConnectAccount}
                            className="bg-[#475162] hover:bg-[#3a4151] text-white font-medium px-6 py-2.5 rounded-xl border border-[#475162] transition-all duration-300 hover:shadow-lg"
                        >
                            <Plus className="mr-2 h-4 w-4" />
                            Conectar Conta
                        </Button>
                    </div>
                </div>

                {authentications.length > 0 && (
                    <div className="flex flex-wrap items-center gap-3">
                        <div className="relative w-72">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <Input
                                placeholder="Buscar perfil por nome ou ID..."
                                className="pl-9"
                                value={buscaPerfil}
                                onChange={(e) => setBuscaPerfil(e.target.value)}
                            />
                        </div>
                        {filtroApp !== null && (
                            <button
                                type="button"
                                onClick={() => setFiltroApp(null)}
                                className="inline-flex items-center gap-1 rounded-full bg-blue-50 border border-blue-200 px-2.5 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100"
                            >
                                App: {filtroApp || 'sem app'}
                                <X className="h-3 w-3" />
                            </button>
                        )}
                        {filtroStatus !== null && (
                            <button
                                type="button"
                                onClick={() => setFiltroStatus(null)}
                                className="inline-flex items-center gap-1 rounded-full bg-blue-50 border border-blue-200 px-2.5 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100"
                            >
                                Status: {filtroStatus === 'ativo' ? 'Ativo' : 'Expirado'}
                                <X className="h-3 w-3" />
                            </button>
                        )}
                        {temFiltroPerfil && (
                            <button type="button" onClick={limparFiltrosPerfil} className="text-xs text-gray-500 hover:text-gray-800 underline">
                                Limpar filtros
                            </button>
                        )}
                        <span className="ml-auto text-sm text-gray-500">
                            {temFiltroPerfil
                                ? <><strong className="text-gray-900">{perfisFiltrados.length}</strong> de {authentications.length} perfis</>
                                : <><strong className="text-gray-900">{authentications.length}</strong> perfis · clique no app ou no status para filtrar</>}
                        </span>
                    </div>
                )}

                <div className="bg-white rounded-lg shadow border border-gray-100 overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                {cabecalhoOrdenavel('perfil', 'Perfil')}
                                {cabecalhoOrdenavel('app', 'App Utilizado')}
                                {cabecalhoOrdenavel('status', 'Status')}
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {perfisFiltrados.length > 0 ? (
                                perfisFiltrados.map((auth) => (
                                    <tr key={auth.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center gap-3">
                                                {auth.picture_url ? (
                                                    <img src={auth.picture_url} alt={auth.meta_user_name} className="h-10 w-10 rounded-full border border-gray-100 object-cover" />
                                                ) : (
                                                    <div className="h-10 w-10 rounded-full bg-blue-50 flex items-center justify-center border border-blue-100">
                                                        <User className="h-5 w-5 text-blue-600" />
                                                    </div>
                                                )}
                                                <div>
                                                    <p className="text-sm font-semibold text-gray-900 leading-tight">{auth.meta_user_name}</p>
                                                    {auth.owner_name && (
                                                        <p className="text-[11px] text-slate-500">de {auth.owner_name}</p>
                                                    )}
                                                    <p className="text-xs text-gray-500 mt-0.5">ID: {auth.meta_user_id}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <button
                                                type="button"
                                                onClick={() => setFiltroApp(atual => atual === (auth.app_name ?? '') ? null : (auth.app_name ?? ''))}
                                                title="Clique para filtrar por este app"
                                            >
                                                <Badge
                                                    variant="outline"
                                                    className={cn(
                                                        'font-medium bg-gray-50 cursor-pointer hover:bg-gray-100',
                                                        filtroApp !== null && filtroApp === (auth.app_name ?? '') && 'ring-1 ring-blue-400'
                                                    )}
                                                >
                                                    {auth.app_name}
                                                </Badge>
                                            </button>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const valor = auth.is_active ? 'ativo' : 'expirado';
                                                    setFiltroStatus(atual => atual === valor ? null : valor);
                                                }}
                                                title="Clique para filtrar por este status"
                                                className={cn(
                                                    'rounded-full cursor-pointer',
                                                    filtroStatus !== null && 'ring-1 ring-blue-400 ring-offset-1'
                                                )}
                                            >
                                                {auth.is_active ? (
                                                    <Badge variant="success">Ativo</Badge>
                                                ) : (
                                                    <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                                                        Expirado
                                                    </Badge>
                                                )}
                                            </button>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right">
                                            <div className="flex justify-end gap-2">
                                                <Button variant="ghost" size="sm" onClick={() => onSync(auth)} title="Sincronizar Páginas" className="h-9 w-9 p-0 text-blue-600 hover:bg-blue-50">
                                                    <RefreshCw className="h-4 w-4" />
                                                </Button>
                                                <Button variant="ghost" size="sm" onClick={() => handleDebugToken(auth)} title="Debug Token" className="h-9 w-9 p-0 text-gray-600 hover:bg-gray-50">
                                                    <Bug className="h-4 w-4" />
                                                </Button>
                                                <Button variant="ghost" size="sm" onClick={() => onRevoke(auth)} title="Remover Conta" className="h-9 w-9 p-0 text-red-600 hover:bg-red-50">
                                                    <XCircle className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={4} className="px-6 py-12 text-center">
                                        <div className="flex flex-col items-center justify-center">
                                            <User className="h-10 w-10 text-gray-300 mb-3" />
                                            <p className="text-gray-500 text-sm">
                                                {authentications.length > 0 ? 'Nenhum perfil com esses filtros.' : 'Nenhuma conta conectada.'}
                                            </p>
                                            {authentications.length > 0 && (
                                                <button type="button" onClick={limparFiltrosPerfil} className="mt-2 text-xs text-blue-600 hover:underline">
                                                    Limpar filtros
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Apps Section */}
            <div className="space-y-6 pt-6 border-t border-gray-100">
                <div className="flex items-center justify-between">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">Configurações de API</h2>
                        <p className="mt-1 text-gray-600">
                            Gerencie as Meta Apps (Client ID / Secrets) cadastradas. Clique em uma linha para ver o uso de rate limit.
                            {/* Só aparece para quem tem app filtrado da lista: sem esta
                                nota, a pessoa não tem como saber que existem outros apps
                                e pode achar que sumiram. */}
                            {appsOcultos > 0 && (
                                <span className="block mt-1 text-sm text-gray-500">
                                    Mostrando os seus apps e os do Sistema.{' '}
                                    {appsOcultos} {appsOcultos === 1 ? 'app de outro usuário está oculto' : 'apps de outros usuários estão ocultos'}.
                                </span>
                            )}
                        </p>
                    </div>
                    <Button onClick={onCreateApp} variant="outline" className="border-gray-200">
                        <Plus className="mr-2 h-4 w-4" />
                        Novo Meta App
                    </Button>
                </div>

                {/* Abas. Os inativos precisam de um lugar próprio: "deletar app"
                    é soft delete, então eles continuam ocupando o app_id e sem
                    esta aba não havia como enxergá-los nem recuperá-los. */}
                <div className="flex gap-1 border-b border-gray-200">
                    {([
                        ['ativos', 'Ativos', ativosVisiveis.length],
                        ['inativos', 'Inativos', inativosVisiveis.length],
                    ] as const).map(([chave, rotulo, qtd]) => (
                        <button
                            key={chave}
                            type="button"
                            onClick={() => setAbaApps(chave)}
                            className={cn(
                                'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
                                abaApps === chave
                                    ? 'border-blue-600 text-blue-700'
                                    : 'border-transparent text-gray-500 hover:text-gray-800'
                            )}
                        >
                            {rotulo}
                            <span className={cn(
                                'ml-2 text-xs rounded-full px-1.5 py-0.5',
                                abaApps === chave ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-500'
                            )}>
                                {qtd}
                            </span>
                        </button>
                    ))}
                </div>

                {abaApps === 'inativos' && (
                    <p className="text-sm text-gray-500">
                        Apps desativados continuam ocupando o <code className="text-xs">app_id</code> no
                        Meta — por isso cadastrar o mesmo ID de novo é recusado. Reative em vez de
                        recriar: as páginas e grupos que já apontavam para o app voltam com ele.
                    </p>
                )}

                <div className="bg-white rounded-lg shadow border border-gray-100 overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Aplicação</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">App ID</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Propósito</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Criado por</th>
                                {(isAdmin || appsVisiveis.some((app) => app.user_id === currentUserId)) && (
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                                )}
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {appsVisiveis.map((app) => (
                                <tr
                                    key={app.id}
                                    onClick={() => setSelectedApp(app)}
                                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                                >
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center gap-3">
                                            <div className="h-10 w-10 rounded-lg bg-gray-50 flex items-center justify-center border border-gray-100">
                                                <Settings className="h-5 w-5 text-gray-500" />
                                            </div>
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <p className="text-sm font-semibold text-gray-900">{app.app_name}</p>
                                                    {app.is_default && <Badge variant="secondary" className="text-[10px] py-0 h-4 bg-gray-100">Padrão</Badge>}
                                                </div>
                                                <p className="text-xs text-gray-500 mt-0.5">v18.0</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 font-mono">
                                        {app.app_id}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <Badge
                                            variant="outline"
                                            className={cn(
                                                'font-medium',
                                                app.purpose === 'broadcast' && 'bg-blue-50 text-blue-700 border-blue-200',
                                                app.purpose === 'funnel' && 'bg-purple-50 text-purple-700 border-purple-200',
                                                (!app.purpose || app.purpose === 'both') && 'bg-gray-50 text-gray-700 border-gray-200'
                                            )}
                                        >
                                            {getPurposeLabel(app)}
                                        </Badge>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {app.is_active ? (
                                            <Badge variant="success">Ativo</Badge>
                                        ) : (
                                            <Badge variant="destructive">Pausado</Badge>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                        {getCreatedByLabel(app)}
                                    </td>
                                    {(() => {
                                        const canEditOrDelete = isAdmin || app.user_id === currentUserId;
                                        if (!canEditOrDelete) return null;
                                        return (
                                            <td className="px-6 py-4 whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                                                <div className="flex justify-end gap-2">
                                                    {/* Reativar é a ação principal da aba de
                                                        inativos: sem ela o app fica preso,
                                                        ocupando o app_id e sem aparecer. */}
                                                    {abaApps === 'inativos' && onReativarApp && (
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() => onReativarApp(app)}
                                                            disabled={reativandoAppId === app.id}
                                                            className="h-9 border-green-200 text-green-700 hover:bg-green-50"
                                                        >
                                                            {reativandoAppId === app.id
                                                                ? <Loader2 className="h-4 w-4 animate-spin" />
                                                                : <Power className="h-4 w-4" />}
                                                            <span className="ml-1.5">Reativar</span>
                                                        </Button>
                                                    )}
                                                    {isAdmin && abaApps === 'ativos' && (
                                                        <Button variant="ghost" size="sm" onClick={() => onToggleAppStatus(app)} title={app.is_active ? "Pausar" : "Ativar"} className="h-9 w-9 p-0">
                                                            <Power className={cn("h-4 w-4", app.is_active ? "text-orange-500" : "text-green-500")} />
                                                        </Button>
                                                    )}
                                                    {canEditOrDelete && (
                                                        <Button variant="ghost" size="sm" onClick={() => onEditApp(app)} title="Editar" className="h-9 w-9 p-0 text-gray-600">
                                                            <Pencil className="h-4 w-4" />
                                                        </Button>
                                                    )}
                                                    {canEditOrDelete && !app.is_default && (
                                                        <Button variant="ghost" size="sm" onClick={() => onDeleteApp(app)} title="Excluir" className="h-9 w-9 p-0 text-red-600">
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                    )}
                                                </div>
                                            </td>
                                        );
                                    })()}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <AppUsageModal app={selectedApp} onOpenChange={(open) => { if (!open) setSelectedApp(null); }} />
        </div>
    );
}
